import { Box } from '@mantine/core';
import { type ReactElement } from 'react';
import markRaw from '@/assets/logo-mark.svg?raw';
import wordmarkRaw from '@/assets/logo-wordmark.svg?raw';

interface LogoProps {
    /** Rendered height in pixels. Width scales with the artwork's aspect ratio. */
    size?: number;
    /** Accessible label. When provided the logo is exposed as an image; otherwise it is decorative. */
    title?: string;
}

/**
 * Force the inline SVG to fill the wrapper's height and scale its width from the
 * viewBox. The artwork's fills reference --ut-logo-fg / --ut-logo-accent, which
 * resolve from the active theme (see AppTheme.ts), so the logo adapts to light/dark.
 */
function fillHeight(raw: string): string {
    return raw.replace('<svg', '<svg style="display:block;height:100%;width:auto"');
}

const MARK_HTML = fillHeight(markRaw);
const WORDMARK_HTML = fillHeight(wordmarkRaw);

function InlineSvg({ html, size, title }: { html: string; size: number; title?: string }): ReactElement {
    return (
        <Box
            role={title ? 'img' : 'presentation'}
            aria-label={title}
            aria-hidden={title ? undefined : true}
            style={{ height: size, display: 'inline-flex', flexShrink: 0 }}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
}

/** UltraTrace brand mark (the hooked-U icon, no wordmark). Scales by height. */
export function UltraTraceLogo({ size = 32, title }: LogoProps): ReactElement {
    return <InlineSvg html={MARK_HTML} size={size} title={title} />;
}

/** UltraTrace full lockup — mark plus the "ultratrace" wordmark. */
export function UltraTraceWordmark({ size = 28, title }: LogoProps): ReactElement {
    return <InlineSvg html={WORDMARK_HTML} size={size} title={title} />;
}
